import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineBell,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const NotificationTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    templateName: '',
    channel: '',
    subject: '',
    body: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadTemplates();
    } else {
      setError('Please login to view notification templates');
      setLoading(false);
    }
  }, [tenantId]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await masterDataService.getNotificationTemplates(tenantId);
      setTemplates(data || []);
    } catch (err) {
      setError('Failed to load notification templates. Please try again.');
      console.error('Error loading notification templates:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredTemplates = templates.filter((template) =>
    template.templateName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    template.channel?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingTemplate(null);
    setFormData({
      templateName: '',
      channel: '',
      subject: '',
      body: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      templateId: template.templateId,
      templateName: template.templateName || '',
      channel: template.channel || '',
      subject: template.subject || '',
      body: template.body || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      // Note: deleteFn is null in config, so we'll skip delete for now
      setError('Delete functionality is not available for notification templates.');
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete notification template. Please try again.');
      console.error('Error deleting notification template:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.templateName.trim()) {
      errors.templateName = 'Template name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const templateData = {
        tenantId: tenantId,
        templateName: formData.templateName.trim(),
        channel: formData.channel.trim() || null,
        subject: formData.subject.trim() || null,
        body: formData.body.trim() || null,
      };

      if (editingTemplate) {
        templateData.templateId = editingTemplate.templateId;
        templateData.templateCode = editingTemplate.templateCode;
        await masterDataService.updateNotificationTemplate(editingTemplate.templateId, templateData);
      } else {
        await masterDataService.createNotificationTemplate(templateData);
      }

      await loadTemplates();
      setShowModal(false);
      setFormData({ templateName: '', channel: '', subject: '', body: '' });
      setEditingTemplate(null);
    } catch (err) {
      let errorMessage = 'Failed to save notification template. Please try again.';
      
      if (err.response?.data) {
        // Handle validation errors object
        if (err.response.data.errors && typeof err.response.data.errors === 'object') {
          // Convert validation errors object to string
          const errorEntries = Object.entries(err.response.data.errors);
          if (errorEntries.length > 0) {
            errorMessage = errorEntries
              .map(([field, messages]) => {
                const msgArray = Array.isArray(messages) ? messages : [messages];
                return `${field}: ${msgArray.join(', ')}`;
              })
              .join('; ');
          } else {
            errorMessage = err.response.data.message || errorMessage;
          }
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
    setFormData({ templateName: '', channel: '', subject: '', body: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading notification templates..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Notification Templates</h1>
          <p>Manage notification templates for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Template
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template' : 'templates'}
          </div>
        </div>

        {filteredTemplates.length === 0 ? (
          <div className="empty-state">
            <HiOutlineBell className="empty-icon" />
            <h3>No templates found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first notification template'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Template
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Template Name</th>
                <th>Channel</th>
                <th>Subject</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((template) => (
                <tr key={template.templateId}>
                  <td>{template.templateId}</td>
                  <td className="name-cell">{template.templateName}</td>
                  <td>{template.channel || '-'}</td>
                  <td>{template.subject || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(template)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingTemplate ? 'Edit Notification Template' : 'Add New Notification Template'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="notification-template-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingTemplate
                  ? 'Updating...'
                  : 'Creating...'
                : editingTemplate
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="notification-template-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="templateName">
                Template Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="templateName"
                value={formData.templateName}
                onChange={(e) =>
                  setFormData({ ...formData, templateName: e.target.value })
                }
                className={formErrors.templateName ? 'error' : ''}
                placeholder="Enter template name"
              />
              {formErrors.templateName && (
                <span className="field-error">{formErrors.templateName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="channel">Channel</label>
              <input
                type="text"
                id="channel"
                value={formData.channel}
                onChange={(e) =>
                  setFormData({ ...formData, channel: e.target.value })
                }
                placeholder="Enter channel (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject</label>
              <input
                type="text"
                id="subject"
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                placeholder="Enter subject (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="body">Body</label>
              <textarea
                id="body"
                value={formData.body}
                onChange={(e) =>
                  setFormData({ ...formData, body: e.target.value })
                }
                placeholder="Enter body text (optional)"
                rows="6"
              />
            </div>
          </form>
        </div>
      </SharedModal>
    </div>
  );
};

export default NotificationTemplatesPage;
